import { FilterSpecification, Layer, useMapInstance } from 'shared/map-exports';
import { useMemo } from 'react';
import { getWatchdutyTileUrl } from 'features/mapping/components/layers/layers.utils';
import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'features/mapping/mapping.utils';
import { BELOW_STREETS_LAYER_ID } from 'features/mapping/styles/constants';
import { MapClickHandler, MapboxFeature } from 'features/mapping/mapping.types';
import { history } from 'shared/history';
import { useSelectedAlertId } from 'features/alerts/hooks/useSelectedAlertId';
import { useAlert } from 'features/alerts/hooks/useAlert';
import { useCenterMap } from 'features/mapping/hooks/useCenterMap';
import { useSelectedGeoEventId } from 'features/geoEvents/hooks/useSelectedGeoEventId';
import { useMapLayersState } from 'state/useMapLayersState';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import {
  NWS_ALERT_FILL_LAYER_ID,
  ALERT_CLICKABLE_LAYER_ID,
  MAX_ZOOM_LEVEL,
  REFRESH_INTERVAL_SECONDS,
  FILL_PATTERN_LAYER_ID,
  STROKE_LAYER_ID,
  SELECTED_FILL_LAYER_ID,
  SELECTED_STROKE_LAYER_ID,
  SELECTED_ALERT_FILL_LAYER_ID,
  SELECTED_ALERT_FILL_PATTERN_LAYER_ID,
  SELECTED_ALERT_STROKE_LAYER_ID,
  NWS_WARNINGS_AND_ALERTS_LAYER_SOURCE,
  WEA_POLYGON_FILL_LAYER_ID,
  WEA_POLYGON_FILL_PATTERN_LAYER_ID,
  WEA_POLYGON_STROKE_LAYER_ID,
  HIGHLIGHT_STROKE_LAYER_ID,
} from './NWSWatchesAndWarningsLayer.constants';
import {
  getFillStyle,
  getPatternFillStyle,
  getPolygonHighlightStrokeStyle,
  getStrokeStyle,
  getClickableFillStyle,
  getWeaFillStyle,
  getWeaPatternFillStyle,
  getWeaStrokeStyle,
} from './NWSWatchesAndWarningsLayer.styles';
import { NWSWarningAlertProperties } from './NWSWatchesAndWarningsLayer.types';
import { sortByRank } from './NWSWatchesAndWarningsLayer.utils';

type NWSWarningsAndAlertsLayerProps = {
  showFireWeather: boolean;
  showFloodWeather: boolean;
  showWeaPolygons: boolean;
  interactive: boolean;
};

const tileOptions = {
  sourceLayer: NWS_WARNINGS_AND_ALERTS_LAYER_SOURCE,
  maxZoom: MAX_ZOOM_LEVEL,
};

// Selected alert is shows beyond max zoom and fills exterior shell (no evac zone cut out)
const selectedAlertTileOptions = {
  sourceLayer: NWS_WARNINGS_AND_ALERTS_LAYER_SOURCE,
  fillShell: true,
};

export const resolveNwsAlertPath = (
  geoJsonFeatures: MapboxFeature<NWSWarningAlertProperties>[],
): string | null => {
  const sortedFeatures = sortByRank(geoJsonFeatures);
  const feature = sortedFeatures[0];
  const featureId = feature.id;
  if (featureId === undefined || featureId === null) return null;
  return `/alert/${featureId}`;
};

export const nwsAlertClickHandler: MapClickHandler<
  NWSWarningAlertProperties
> = (geoJsonFeatures): void => {
  const path = resolveNwsAlertPath(geoJsonFeatures);
  if (path) history.push(path);
};

export const NWSWarningsAndAlertsLayer = (
  props: NWSWarningsAndAlertsLayerProps,
): JSX.Element | null => {
  const { showFireWeather, showFloodWeather, showWeaPolygons, interactive } =
    props;
  const activeAlertId = useSelectedAlertId();
  const { alert } = useAlert({ id: activeAlertId });
  const mapInstance = useMapInstance();
  const selectedGeoEventId = useSelectedGeoEventId();
  const { isDarkBaseLayer } = useMapLayersState();

  const alreadyVisible = useMemo(() => {
    if (!mapInstance || !activeAlertId) return null;
    // Skip layers not yet rendered in the map style to avoid query errors
    const layers = [
      NWS_ALERT_FILL_LAYER_ID,
      WEA_POLYGON_FILL_LAYER_ID,
      SELECTED_ALERT_FILL_LAYER_ID,
    ].filter((id) => mapInstance.getLayer(id));
    if (!layers.length) return null;
    return (
      mapInstance.queryRenderedFeatures({
        layers,
        filter: ['==', ['id'], activeAlertId],
      }).length > 0
    );
  }, [activeAlertId, mapInstance]);

  useCenterMap({
    latLng: alreadyVisible ? null : (alert?.center ?? null),
    drawerIsOpen: true,
    defaultSnapPointPct: 0.47,
    zoom: 8,
    zoomIn: true,
  });

  const allowedTypes = useMemo(() => {
    const types: string[] = [];
    if (showFireWeather) {
      types.push('nws_fire');
    }
    if (showFloodWeather) {
      types.push('nws_flood');
    }
    return types;
  }, [showFireWeather, showFloodWeather]);

  const filterExpression: FilterSpecification = [
    'in',
    ['get', 'type'],
    ['literal', allowedTypes],
  ];

  const selectedId = activeAlertId ?? '';
  const selectedFilterExpression: FilterSpecification = [
    'all',
    filterExpression,
    ['==', ['id'], selectedId],
  ];
  // Hide the selected alert from the base layers so it isn't drawn twice
  // underneath the selected-* layers that render it.
  const unselectedFilterExpression: FilterSpecification = [
    'all',
    filterExpression,
    ['!=', ['id'], selectedId],
  ];

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: ['id'],
    selectedId,
    otherLayerProps: {
      'source-layer': NWS_WARNINGS_AND_ALERTS_LAYER_SOURCE,
    },
  });

  const clickableFillStyle = getClickableFillStyle(
    filterExpression,
    showWeaPolygons,
    { sourceLayer: NWS_WARNINGS_AND_ALERTS_LAYER_SOURCE },
  );

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: ['id'],
    selectedId,
    otherLayerProps: {
      'source-layer': NWS_WARNINGS_AND_ALERTS_LAYER_SOURCE,
    },
  });

  const visible = showFireWeather || showFloodWeather || showWeaPolygons;

  return (
    <VisibilityToggledSource
      visible={visible}
      url={getWatchdutyTileUrl(NWS_WARNINGS_AND_ALERTS_LAYER_SOURCE)}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
    >
      <Layer
        id={NWS_ALERT_FILL_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getFillStyle(
          unselectedFilterExpression,
          isDarkBaseLayer,
          selectedGeoEventId,
          tileOptions,
        )}
      />
      <Layer
        id={FILL_PATTERN_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getPatternFillStyle(
          unselectedFilterExpression,
          isDarkBaseLayer,
          selectedGeoEventId,
          tileOptions,
        )}
      />
      <Layer
        id={STROKE_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getStrokeStyle(
          unselectedFilterExpression,
          isDarkBaseLayer,
          selectedGeoEventId,
          tileOptions,
        )}
      />
      <Layer
        id={ALERT_CLICKABLE_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        metadata={{ interactive }}
        {...clickableFillStyle}
      />

      <Layer
        id={WEA_POLYGON_FILL_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getWeaFillStyle(showWeaPolygons, selectedGeoEventId, tileOptions)}
      />
      <Layer
        id={WEA_POLYGON_FILL_PATTERN_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getWeaPatternFillStyle(showWeaPolygons, tileOptions)}
      />
      <Layer
        id={WEA_POLYGON_STROKE_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getWeaStrokeStyle(showWeaPolygons, tileOptions)}
      />

      <Layer
        id={SELECTED_ALERT_FILL_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getFillStyle(
          selectedFilterExpression,
          isDarkBaseLayer,
          selectedGeoEventId,
          selectedAlertTileOptions,
        )}
      />
      <Layer
        id={SELECTED_ALERT_FILL_PATTERN_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getPatternFillStyle(
          selectedFilterExpression,
          isDarkBaseLayer,
          selectedGeoEventId,
          selectedAlertTileOptions,
        )}
      />
      <Layer
        id={SELECTED_ALERT_STROKE_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getStrokeStyle(
          selectedFilterExpression,
          isDarkBaseLayer,
          selectedGeoEventId,
          selectedAlertTileOptions,
        )}
      />

      <Layer
        id={HIGHLIGHT_STROKE_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getPolygonHighlightStrokeStyle(
          showFireWeather,
          showFloodWeather,
          selectedId,
          tileOptions,
        )}
      />

      <Layer
        id={SELECTED_FILL_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...selectedFillStyle}
      />
      <Layer
        id={SELECTED_STROKE_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...selectedStrokeStyle}
      />
    </VisibilityToggledSource>
  );
};
